import { t } from '../i18n/translations';
import { ChatMessage, Participant } from '../types';
import { exportChatToPDF } from '../services/chatExport';

export class ChatDrawer {
  private messages: ChatMessage[] = [];
  private participants: Participant[] = [];
  private currentUserId = '';
  private currentUserName = '';
  private roomId = '';
  private roomTitle = '';
  private selectedTargetId = '';
  private pendingAttachment: { url: string; name: string; type: string; size?: number } | null = null;
  private containerEl: HTMLElement | null = null;
  private isVisible = false;

  public onSendMessage?: (content: string, targetId?: string, attachment?: { url: string; name: string; type: string; size?: number }) => void;
  public onClose?: () => void;

  public setCurrentUserId(id: string) {
    this.currentUserId = id;
  }

  public setMeetingInfo(roomId: string, title?: string, userName?: string) {
    this.roomId = roomId;
    if (title) this.roomTitle = title;
    if (userName) this.currentUserName = userName;
  }

  public setParticipants(list: Participant[]) {
    this.participants = list;
    this.updateTargetSelector();
  }

  public addMessage(msg: ChatMessage) {
    this.messages.push(msg);
    this.appendMessageToDOM(msg);
  }

  public toggle(container: HTMLElement) {
    this.isVisible = !this.isVisible;
    if (this.isVisible) {
      this.render(container);
    } else {
      container.innerHTML = '';
    }
  }

  public hide() {
    this.isVisible = false;
    if (this.containerEl) {
      this.containerEl.innerHTML = '';
    }
  }

  public getIsOpen(): boolean {
    return this.isVisible;
  }

  public render(container: HTMLElement) {
    this.containerEl = container;

    container.innerHTML = `
      <div class="side-drawer">
        <div class="drawer-header">
          <h3>${t('chat')}</h3>
          <div style="display: flex; align-items: center; gap: 8px;">
            <button id="btn-export-chat-pdf" class="btn-secondary" style="height: 30px; padding: 0 10px; font-size: 0.78rem; display: inline-flex; align-items: center; gap: 4px; border-radius: var(--radius-sm); border: 1px solid var(--border-subtle); background: var(--bg-surface-elevated);" title="Export Chat Transcript with Screenshots to High-Resolution PDF">
              <span style="font-size: 0.85rem;">📄</span>
              <span>Export PDF</span>
            </button>
            <button id="btn-close-chat" class="control-btn" style="width: 32px; height: 32px;" title="Close chat">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <!-- Target Selector (Public or Direct) -->
        <div style="padding: 8px 1.25rem; border-bottom: 1px solid var(--border-subtle); background: var(--bg-surface-elevated); display: flex; align-items: center; gap: 8px;">
          <span style="font-size: 0.8rem; color: var(--text-muted);">To:</span>
          <select id="chat-recipient-select" class="form-input" style="padding: 4px 8px; font-size: 0.8rem; flex: 1;">
            <option value="">Everyone (Public)</option>
            ${this.participants
              .filter(p => p.id !== this.currentUserId)
              .map(p => `<option value="${p.id}" ${this.selectedTargetId === p.id ? 'selected' : ''}>${p.display_name} (Private)</option>`)
              .join('')}
          </select>
        </div>

        <!-- Messages List -->
        <div id="chat-messages-container" class="drawer-body">
          ${this.messages.map(m => this.renderMessageHTML(m)).join('')}
        </div>

        <!-- Attachment Preview Bar -->
        <div id="chat-attachment-preview" style="display: none; padding: 6px 12px; background: var(--bg-surface-elevated); border-top: 1px solid var(--border-subtle); align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 8px; overflow: hidden; max-width: 80%;">
            <span id="chat-attachment-icon" style="font-size: 1rem;">📎</span>
            <span id="chat-attachment-name" style="font-size: 0.8rem; font-weight: 600; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;"></span>
          </div>
          <button type="button" id="btn-cancel-attachment" style="background: none; border: none; color: var(--color-danger); cursor: pointer; font-size: 0.9rem; padding: 2px 6px;">✕</button>
        </div>

        <!-- Input Bar -->
        <form id="chat-input-form" class="chat-input-bar">
          <label class="control-btn" style="width: 36px; height: 36px; margin: 0; cursor: pointer; flex-shrink: 0;" title="Attach image, screenshot, or document">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
            </svg>
            <input type="file" id="chat-file-input" style="display: none;" />
          </label>
          <input type="text" id="chat-input-field" class="form-input" placeholder="${t('typeMessage')} (or paste screenshot)" style="flex: 1;" maxlength="500" autocomplete="off" />
          <button type="submit" class="btn-primary" style="width: auto; padding: 0 16px;">
            ${t('send')}
          </button>
        </form>
      </div>
    `;

    this.bindEvents(container);
    this.scrollToBottom();
  }

  private renderMessageHTML(msg: ChatMessage): string {
    const isSelf = msg.sender_id === this.currentUserId;
    const timeStr = new Date(msg.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const isImg = msg.attachment_url && (msg.attachment_type === 'image' || msg.attachment_name?.match(/\.(png|jpe?g|gif|webp|svg)$/i));

    return `
      <div class="chat-msg ${isSelf ? 'self' : ''} ${msg.is_private ? 'private' : ''}">
        <div class="chat-msg-header">
          <span class="chat-msg-sender">${isSelf ? 'You' : msg.sender_name} ${msg.is_private ? '(Direct Message)' : ''}</span>
          <span>${timeStr}</span>
        </div>
        <div class="chat-msg-bubble">
          ${msg.content ? `<div>${msg.content}</div>` : ''}

          ${isImg && msg.attachment_url ? `
            <div style="margin-top: ${msg.content ? '8px' : '0'}; border-radius: var(--radius-md); overflow: hidden; max-width: 260px;">
              <a href="${msg.attachment_url}" target="_blank" rel="noopener noreferrer">
                <img src="${msg.attachment_url}" alt="${msg.attachment_name || 'Image'}" style="width: 100%; max-height: 200px; object-fit: contain; background: #000; border-radius: var(--radius-sm); display: block;" />
              </a>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 4px;">
                <span style="font-size: 0.725rem; color: var(--text-muted); text-overflow: ellipsis; overflow: hidden; white-space: nowrap; max-width: 160px;">${msg.attachment_name || 'Screenshot'}</span>
                <a href="${msg.attachment_url}" download="${msg.attachment_name || 'screenshot.png'}" style="font-size: 0.725rem; color: var(--brand-primary); text-decoration: underline; font-weight: 600;">Download</a>
              </div>
            </div>
          ` : ''}

          ${!isImg && msg.attachment_url ? `
            <div style="margin-top: ${msg.content ? '8px' : '0'}; padding: 8px 12px; background: rgba(255, 255, 255, 0.08); border-radius: var(--radius-md); display: flex; align-items: center; justify-content: space-between; gap: 8px;">
              <div style="display: flex; align-items: center; gap: 6px; overflow: hidden;">
                <span>📄</span>
                <span style="font-size: 0.8rem; font-weight: 500; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; max-width: 140px;">${msg.attachment_name || 'File'}</span>
              </div>
              <a href="${msg.attachment_url}" download="${msg.attachment_name || 'file'}" class="btn-secondary" style="font-size: 0.7rem; padding: 3px 8px; flex-shrink: 0; text-decoration: none;">Download</a>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  private appendMessageToDOM(msg: ChatMessage) {
    if (!this.containerEl) return;
    const body = this.containerEl.querySelector('#chat-messages-container');
    if (body) {
      body.insertAdjacentHTML('beforeend', this.renderMessageHTML(msg));
      this.scrollToBottom();
    }
  }

  private scrollToBottom() {
    if (!this.containerEl) return;
    const body = this.containerEl.querySelector('#chat-messages-container');
    if (body) {
      body.scrollTop = body.scrollHeight;
    }
  }

  private updateTargetSelector() {
    if (!this.containerEl) return;
    const select = this.containerEl.querySelector('#chat-recipient-select') as HTMLSelectElement;
    if (select) {
      const currentVal = select.value;
      select.innerHTML = `
        <option value="">Everyone (Public)</option>
        ${this.participants
          .filter(p => p.id !== this.currentUserId)
          .map(p => `<option value="${p.id}" ${currentVal === p.id ? 'selected' : ''}>${p.display_name} (Private)</option>`)
          .join('')}
      `;
    }
  }

  private bindEvents(container: HTMLElement) {
    container.querySelector('#btn-close-chat')?.addEventListener('click', () => {
      this.hide();
      if (this.onClose) this.onClose();
    });

    // Export Chat Transcript to High-Resolution PDF
    container.querySelector('#btn-export-chat-pdf')?.addEventListener('click', async () => {
      if (this.messages.length === 0) {
        alert('No chat messages to export yet.');
        return;
      }

      const btn = container.querySelector('#btn-export-chat-pdf') as HTMLButtonElement;
      const originalHTML = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span style="font-size: 0.85rem;">⏳</span><span>Generating PDF...</span>';

      try {
        await exportChatToPDF(this.messages, {
          roomId: this.roomId || 'meeting',
          title: this.roomTitle || 'Verve Conference',
          exportedBy: this.currentUserName || 'Participant',
        });
      } catch (err) {
        console.error('Failed to export chat to PDF:', err);
        alert('Failed to export chat to PDF: ' + err);
      } finally {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      }
    });

    const select = container.querySelector('#chat-recipient-select') as HTMLSelectElement;
    select?.addEventListener('change', () => {
      this.selectedTargetId = select.value;
    });

    const fileInput = container.querySelector('#chat-file-input') as HTMLInputElement;
    fileInput?.addEventListener('change', () => {
      const file = fileInput.files?.[0];
      if (!file) return;
      this.processFileAttachment(file, container);
    });

    // Paste screenshot support
    const inputField = container.querySelector('#chat-input-field') as HTMLInputElement;
    inputField?.addEventListener('paste', (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (file) {
            e.preventDefault();
            this.processFileAttachment(file, container, `screenshot-${Date.now()}.png`);
            break;
          }
        }
      }
    });

    container.querySelector('#btn-cancel-attachment')?.addEventListener('click', () => {
      this.pendingAttachment = null;
      const preview = container.querySelector('#chat-attachment-preview') as HTMLElement;
      if (preview) preview.style.display = 'none';
      if (fileInput) fileInput.value = '';
    });

    container.querySelector('#chat-input-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = container.querySelector('#chat-input-field') as HTMLInputElement;
      const text = input?.value.trim() || '';
      if ((text || this.pendingAttachment) && this.onSendMessage) {
        this.onSendMessage(text, this.selectedTargetId || undefined, this.pendingAttachment || undefined);
        input.value = '';
        this.pendingAttachment = null;
        const preview = container.querySelector('#chat-attachment-preview') as HTMLElement;
        if (preview) preview.style.display = 'none';
        if (fileInput) fileInput.value = '';
      }
    });
  }

  private processFileAttachment(file: File, container: HTMLElement, customName?: string) {
    const isImage = file.type.startsWith('image/');
    const fileName = customName || file.name;

    // Up to 8MB allowed
    if (file.size > 8 * 1024 * 1024) {
      alert('File size exceeds 8MB limit for real-time transfer.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (re) => {
      const result = re.target?.result as string;
      if (isImage) {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let w = img.width;
          let h = img.height;
          // Support 4K high resolution for crisp screenshot readability
          const maxDim = 3840;
          if (w > maxDim || h > maxDim) {
            if (w > h) {
              h = Math.round((h * maxDim) / w);
              w = maxDim;
            } else {
              w = Math.round((w * maxDim) / h);
              h = maxDim;
            }
          }
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, w, h);
            // High fidelity JPEG output
            const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
            this.setAttachmentPreview(dataUrl, fileName, 'image', container);
          }
        };
        img.src = result;
      } else {
        this.setAttachmentPreview(result, fileName, 'file', container);
      }
    };
    reader.readAsDataURL(file);
  }

  private setAttachmentPreview(url: string, name: string, type: string, container: HTMLElement) {
    this.pendingAttachment = { url, name, type };
    const preview = container.querySelector('#chat-attachment-preview') as HTMLElement;
    const nameEl = container.querySelector('#chat-attachment-name') as HTMLElement;
    const iconEl = container.querySelector('#chat-attachment-icon') as HTMLElement;

    if (preview && nameEl && iconEl) {
      nameEl.textContent = name;
      iconEl.textContent = type === 'image' ? '🖼️' : '📎';
      preview.style.display = 'flex';
    }
  }
}
